import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { fbt } from 'fbtee'
import { openFeedback, registerFeedbackHandler, type FeedbackContext } from '@/lib/feedbackBridge'
import { startViewportCapture } from '@/lib/screenCapture'
import { track } from '@/lib/analytics'
import { useAuth } from '@/hooks/useAuth'
import { useShakeGesture } from '@/hooks/useShakeGesture'
import { FeedbackForm } from '@/components/FeedbackForm'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

export function FeedbackDialog() {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const [context, setContext] = useState<FeedbackContext | null>(null)
  const [formKey, setFormKey] = useState(0)
  const capturing = useRef(false)

  // The dialog opens only once the page has been cloned, so the screenshot
  // shows what the user was looking at, not the dialog. Rendering the clone
  // takes seconds on a big schedule; the form shows a placeholder meanwhile.
  const handleShake = useCallback(async () => {
    if (!user || open || capturing.current) return
    capturing.current = true
    try {
      const { screenshot } = await startViewportCapture()
      void screenshot.then(blob => track('feedback_shake_opened', { had_screenshot: Boolean(blob) }))
      openFeedback({ category: 'bug', screenshot, entryPoint: 'shake' })
    } finally {
      capturing.current = false
    }
  }, [user, open])
  useShakeGesture(handleShake)

  useEffect(() => {
    return registerFeedbackHandler(ctx => {
      setContext(ctx ?? null)
      setFormKey(k => k + 1)
      setOpen(true)
    })
  }, [])

  if (!user) return null

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <fbt desc="Title of the send feedback dialog">Report an issue</fbt>
          </DialogTitle>
          <DialogDescription>
            <fbt desc="Description in the feedback dialog explaining what kind of feedback is welcome">
              Bug reports, feature ideas, anything else — straight to the dev.
            </fbt>
          </DialogDescription>
        </DialogHeader>
        <FeedbackForm
          key={formKey}
          errorContext={context?.error}
          initialCategory={context?.category}
          pendingScreenshot={context?.screenshot}
          entryPoint={context?.entryPoint}
          onSuccess={() => {
            toast.success(fbt('Thanks for the feedback!', 'Toast message after successfully submitting feedback'))
            setOpen(false)
          }}
        />
      </DialogContent>
    </Dialog>
  )
}
